import type { SseFrame } from '../../types/xoe/XoeStreamTypes';
/**
 * @file SSE frame parsing — a pure function over decoded string chunks.
 *
 * Deliberately knows NOTHING about fetch, ReadableStream, Xoe, turns or tools. It takes
 * text and yields frames, so it can be tested exhaustively without a network or a
 * browser stream implementation (jsdom provides neither `fetch` nor `ReadableStream`).
 * `XoeStreamApi.ts` owns the transport; this owns the grammar.
 *
 * THE FRAME RULES, from `xoe-agent/app/sse.py`
 * --------------------------------------------
 * 1. **A frame is terminated by a BLANK LINE.** Not by a chunk boundary. A chunk can
 *    carry half a frame, three frames, or three-and-a-half — the network decides, and
 *    it decides differently under load than it does on localhost. This parser therefore
 *    BUFFERS across calls, which is the single most important thing it does.
 * 2. **`data:` is LINE-ORIENTED.** A payload containing a newline is split across
 *    several `data:` lines and must be re-joined with `\n`.
 * 3. **`:`-prefixed lines are comments** — the 15s heartbeat. Skipped.
 *
 * FORWARD COMPATIBILITY: an unrecognised `event:` name is returned like any other and
 * left for the consumer to ignore. It is NEVER thrown. Every server payload carries
 * `"v": 1` precisely so a later version can add an event without a coordinated frontend
 * release, and a parser that threw on the unknown would defeat that entirely.
 *
 * A malformed JSON payload drops that ONE frame and keeps reading. Mid-stream the HTTP
 * status is already 200, so throwing would discard the rest of the turn — including the
 * terminal frame that carries the practitioner's actual answer.
 */

/**
 * Create a stateful parser. Feed it decoded chunks in order; it returns the frames that
 * completed in that chunk (possibly none).
 *
 * State is per-instance, never module-level: one turn's truncated tail must not be able
 * to corrupt the next turn's first frame.
 */
export const createSseParser = (): ((chunk: string) => SseFrame[]) => {
	let buffer = '';

	return (chunk: string): SseFrame[] => {
		buffer += chunk;

		const frames: SseFrame[] = [];

		// Normalise CRLF so the blank-line split below is a single rule.
		buffer = buffer.replace(/\r\n/g, '\n');

		let separatorIndex = buffer.indexOf('\n\n');
		while (separatorIndex !== -1) {
			const rawFrame = buffer.slice(0, separatorIndex);
			buffer = buffer.slice(separatorIndex + 2);

			const frame = parseFrame(rawFrame);
			if (frame) frames.push(frame);

			separatorIndex = buffer.indexOf('\n\n');
		}

		return frames;
	};
};

/** Parse one complete frame body (no terminator). Returns null for anything unusable. */
const parseFrame = (raw: string): SseFrame | null => {
	let event = '';
	const dataLines: string[] = [];

	for (const line of raw.split('\n')) {
		// Comment / heartbeat.
		if (line.startsWith(':')) continue;

		const colon = line.indexOf(':');
		if (colon === -1) continue;

		const field = line.slice(0, colon);
		// The single space after the colon is optional in the SSE grammar.
		let value = line.slice(colon + 1);
		if (value.startsWith(' ')) value = value.slice(1);

		if (field === 'event') {
			event = value;
		} else if (field === 'data') {
			dataLines.push(value);
		}
		// `id:` and `retry:` are grammatical but unused by this server — ignored.
	}

	if (!event || dataLines.length === 0) return null;

	try {
		const parsed: unknown = JSON.parse(dataLines.join('\n'));
		if (typeof parsed !== 'object' || parsed === null) return null;
		return { event, data: parsed as Record<string, unknown> };
	} catch {
		// Drop this frame; keep the stream. See the module docstring.
		return null;
	}
};
